import {
  CloseDrawerButton,
  Drawer,
  DrawerOverlay,
  DrawerOverlayProvider,
  DrawerProps,
  Step,
  Stepper,
  SwipeableViews,
  useDrawerInstance,
  useSwipeableViews,
  ExitConfirmationOverlay,
  Typography,
  Stack,
} from '@cast/design-system';

import { ProvisionNodesDrawerProvider } from './ProvisionNodesDrawerProvider';
import { ProvisionNodesDrawerSlides } from './types';
import { useSteps } from './useSteps';
import { EnhancedEdgeLocation } from '../../types';

export type ProvisionNodesDrawerProps = DrawerProps & {
  edgeLocation?: EnhancedEdgeLocation;
  clusterId?: string;
};

const ExitConfirmation = () => {
  const { close, setShouldRenderGuard, shouldRenderGuard } =
    useDrawerInstance();

  if (!shouldRenderGuard) {
    return null;
  }

  return (
    <DrawerOverlay>
      <ExitConfirmationOverlay
        title="Are you sure you want to leave?"
        body="Your changes will be lost."
        onLeave={() => close()}
        onContinue={() => setShouldRenderGuard(false)}
        continueLabel="Continue"
      />
    </DrawerOverlay>
  );
};

const ProvisionNodesDrawerBody = ({
  slideKey,
  children,
}: {
  slideKey: string;
  children: React.ReactNode;
}) => {
  const swipeableViews = useSwipeableViews();
  const isActive = swipeableViews?.currentSlide === slideKey;

  return (
    <Stack sx={{ height: '100%', px: 0 }}>
      <Typography variant="h5" pb={16} px={32}>
        Provision an edge node
      </Typography>
      {isActive && children}
    </Stack>
  );
};

const ProvisionNodesDrawerContent = () => {
  const steps = useSteps();

  const visibleSteps = steps.filter((step) => step.show);
  const slides = visibleSteps.reduce((acc, step) => {
    acc[step.key] = (
      <ProvisionNodesDrawerBody slideKey={step.key}>
        {step.node}
      </ProvisionNodesDrawerBody>
    );
    return acc;
  }, {} as Record<ProvisionNodesDrawerSlides, React.ReactNode>);

  return (
    <>
      <SwipeableViews
        sx={{
          flex: 1,
        }}
        slidesWrapperSx={{
          height: '100%',
        }}
        slideWrapperSx={{
          height: '100%',
        }}
        animateHeight={false}
        contentBefore={({ currentSlideIndex }) => (
          <Stack
            height={80}
            pt={20}
            position="relative"
            justifyContent="flex-end"
          >
            <CloseDrawerButton />
            {visibleSteps.length > 1 && (
              <Stepper
                sx={{
                  width: 220,
                  position: 'absolute',
                  top: 20,
                  right: 58,
                }}
                activeStep={currentSlideIndex}
                size="small"
              >
                {visibleSteps.map((step) => (
                  <Step key={step.key} label={step.label} />
                ))}
              </Stepper>
            )}
          </Stack>
        )}
        slides={slides}
      />
      <ExitConfirmation />
    </>
  );
};

export const ProvisionNodesDrawer = ({
  edgeLocation,
  clusterId,
  ref,
  ...props
}: ProvisionNodesDrawerProps) => {
  return (
    <Drawer ref={ref} testId="provision-nodes-drawer" size="large" {...props}>
      <DrawerOverlayProvider>
        <ProvisionNodesDrawerProvider
          edgeLocation={edgeLocation}
          clusterId={clusterId}
        >
          <ProvisionNodesDrawerContent />
        </ProvisionNodesDrawerProvider>
      </DrawerOverlayProvider>
    </Drawer>
  );
};
